/**
 * The ONE actor-database lifecycle, for both platforms.
 *
 * Every open and close of the actor connection goes through this state machine, so
 * there is exactly one place that can hold a live connection and exactly one
 * transition into "open". That is what makes a second open of a still-owned
 * database impossible: callers never open, they ask for the current state.
 *
 *   closed ──load──▶ opening ──ok──▶ open ──close──▶ closing ──▶ closed
 *                       │              │   ▲              │
 *                       │       damaged│   └──rejected────┤ (retained)
 *                       ▼              ▼                  │
 *                    failed ◀──────────┴───timed out──────┘ (abandoned)
 *                       └──close (recover)──▶ closed
 *
 * `failed` is a LATCH. A failed open is never retried by the next query; it stays
 * failed, with its error, until an explicit close/recover. Without the latch every
 * suspense query that observed the failure re-ran the open, and one refused native
 * lease turned into dozens of refused opens per second.
 *
 * ## Nothing here waits forever
 *
 * Every transition that waits on the engine has a deadline, and every expiry lands in `failed`
 * — the state the app already renders as a recovery screen — never in an endless load:
 *
 *   - OPEN ({@link ACTOR_DATABASE_OPEN_DEADLINE_MS}). A timed-out open latches `failed`. Its work
 *     cannot be cancelled, so a connection it produces later is adopted into that failed state
 *     (recovery closes it) or, if the lifecycle has moved on, closed — never leaked, never
 *     published.
 *   - CLOSE ({@link ACTOR_DATABASE_CLOSE_DEADLINE_MS}). A close that REJECTS was answered: the
 *     handle is retained exactly as before. A close that never ANSWERS is abandoned: the
 *     lifecycle stops referencing the handle and latches `failed`. Abandoning is not releasing —
 *     the engine keeps its native ownership lease until its own close confirms in the background
 *     (`open-owned-turso-generation.ts`), so no replacement can ever run beside it; a reopen
 *     succeeds once that happens, and a restart always ends it.
 *   - DAMAGE. When an operation on the open connection outlives its deadline (a poisoned
 *     connection lock or a stalled engine call), the connection reports it through
 *     `reportDamage` and the lifecycle latches `failed` at once, so the recovery UI replaces it
 *     instead of every query failing one by one.
 *   - PAUSE ({@link ACTOR_DATABASE_PAUSE_MAX_HOLD_MS}). See `runWithLoadsPaused`.
 *
 * Pure: the open and close operations are injected, so the machine is testable
 * without a native engine and the same code runs on native and web.
 */

import {
  boundedPromise,
  DeadlineExceededError,
  isDeadlineExceededError,
  withDeadline,
} from "@-/dev.improve.haniverse.lib.deadline";

/**
 * The backstop for one whole open: engine open, validation, guest handoff and migrations. Every
 * step has its own bound; this only fires when one of them failed to, so it must not cut a path
 * whose steps are all still within theirs. The longest such path is a native sign-up: cloud
 * bootstrap connect (60s) + validation and reconnect (well under 20s in practice) + the replay's
 * cloud schema check (8s) and pull (30s) + the handoff push (30s) ≈ 150s. Web's open is capped at
 * 45s by its own opener.
 */
export const ACTOR_DATABASE_OPEN_DEADLINE_MS = 150_000;

/**
 * How long a close may go unanswered before the handle is abandoned. Above the layered bounds
 * beneath it (the close-safe wrapper's 5s drain + 20s native close), so it only fires when one of
 * those failed to.
 */
export const ACTOR_DATABASE_CLOSE_DEADLINE_MS = 30_000;

/**
 * The longest `runWithLoadsPaused` may hold loads. The slowest legitimate held operation is a
 * sign-in: a safety backup of a large study database, then a bounded close and a file reset. Past
 * this the operation is presumed wedged: its caller is rejected, and the lifecycle latches `failed`
 * rather than letting loads open against a half-swapped database.
 */
export const ACTOR_DATABASE_PAUSE_MAX_HOLD_MS = 120_000;

/**
 * How long one load waits behind a pause before failing with a retryable timeout. Matched to the
 * central query backstop (30s): a Suspense read waiting longer has no awaiter left anyway, and a
 * load issued from INSIDE the paused operation (which would wait on itself) fails here instead of
 * holding the operation until its max hold.
 */
export const ACTOR_DATABASE_PAUSED_LOAD_WAIT_MS = 30_000;

/**
 * Slack for the pause's own hold promise beyond the max hold. The hold is released by
 * `runWithLoadsPaused`'s `finally`; this only guarantees it cannot outlive that by a bug.
 */
const PAUSE_HOLD_BACKSTOP_MARGIN_MS = 5000;

interface LoadedConnection<Connection> {
  connection: Connection;
}

/** One open attempt: identity for late damage reports and late (timed-out) results. */
interface OpenAttempt<Connection> {
  damage: unknown;
  owned: Connection | null;
}

type LifecycleState<Connection, Loaded extends LoadedConnection<Connection>> =
  | { kind: "closed" }
  | { attempt: OpenAttempt<Connection>; kind: "opening"; promise: Promise<Loaded> }
  | { attempt: OpenAttempt<Connection> | null; kind: "open"; loaded: Loaded }
  | { kind: "closing"; promise: Promise<void> }
  | {
      attempt: OpenAttempt<Connection> | null;
      connection: Connection | null;
      error: unknown;
      kind: "failed";
    };

/** The connection stopped answering and was abandoned; recovery reopens. */
export class ActorDatabaseDamagedError extends Error {
  constructor(cause: unknown) {
    super(
      `The study database stopped responding (an operation timed out and may still be running), so Han stopped using that connection. Try again to reopen it.`,
      { cause },
    );
    this.name = "ActorDatabaseDamagedError";
  }
}

/** A close never answered; the handle was abandoned (it finishes closing in the background). */
export class ActorDatabaseCloseAbandonedError extends Error {
  constructor(cause: unknown) {
    super(
      `Closing the study database timed out; Han abandoned that connection, which finishes closing in the background. Try again in a moment, or restart Han if this persists.`,
      { cause },
    );
    this.name = "ActorDatabaseCloseAbandonedError";
  }
}

/** A paused operation (an account switch) did not finish; loads stay refused until recovery. */
export class ActorDatabaseSwapTimeoutError extends Error {
  constructor(cause: unknown) {
    super(
      `Switching the study database timed out before it finished. Try again, or restart Han if this persists.`,
      { cause },
    );
    this.name = "ActorDatabaseSwapTimeoutError";
  }
}

/** A load or pause issued from inside the paused operation, which would wait on itself. */
export class ReentrantActorDatabasePauseError extends Error {
  constructor(what: "load" | "pause") {
    super(
      what === "load"
        ? "A study-database load was issued from inside runWithLoadsPaused; it would wait for its own pause. Use the `paused.load()` handed to the operation."
        : "runWithLoadsPaused was called from inside a paused operation; it would wait for its own pause. Use the `paused.runWithLoadsPaused()` handed to the operation.",
    );
    this.name = "ReentrantActorDatabasePauseError";
  }
}

/** What a paused operation may use to reach the lifecycle without waiting on its own pause. */
export interface PausedActorDatabaseLifecycle<Loaded> {
  /** Load (opening if needed) past the pause this operation holds. */
  load(): Promise<Loaded>;
  /** Run a nested operation inside the pause this operation already holds. */
  runWithLoadsPaused<T>(operation: () => Promise<T>): Promise<T>;
}

export interface ActorDatabaseLifecycleOptions<
  Connection,
  Loaded extends LoadedConnection<Connection>,
> {
  /** Confirm-or-throw close. A throw means ownership is retained. */
  close: (connection: Connection) => Promise<void>;
  /** Override the deadlines (tests). */
  deadlines?: Partial<{
    closeMs: number;
    openMs: number;
    pausedLoadWaitMs: number;
    pauseMaxHoldMs: number;
  }>;
  onError?: (message: string, error: unknown) => void;
  /**
   * Open the connection and prepare it (migrations). Call `own(connection)` the
   * moment the engine handle exists so a later failure can still close it. Wire
   * `reportDamage` to the connection's damage signal: it latches `failed` so the
   * connection is replaced.
   */
  open: (
    own: (connection: Connection) => void,
    reportDamage: (error: unknown) => void,
  ) => Promise<Loaded>;
}

export interface ActorDatabaseLifecycle<Connection, Loaded extends LoadedConnection<Connection>> {
  /** The only path out of `open` and `failed`. Rethrows and retains on an unconfirmed close. */
  close(): Promise<void>;
  /** Forget the connection WITHOUT closing it (test teardown only). */
  forget(options?: { notify?: boolean }): void;
  /**
   * The latched open error, or `undefined` unless the lifecycle is `failed`. Stable while
   * failed, so it can back a `useSyncExternalStore` snapshot: the UI must show a failed open
   * and its recovery, never an endless loading state.
   */
  getFailure(): unknown;
  getLoaded(): Loaded | undefined;
  /** The open connection, joining an in-flight open; rejects with the latched error while failed. */
  load(): Promise<Loaded>;
  /** Publish an externally opened connection (test rigs). */
  publish(loaded: Loaded, options?: { notify?: boolean }): void;
  /**
   * Hold new opens until `operation` settles; in-flight opens are unaffected. Bounded: see
   * {@link ACTOR_DATABASE_PAUSE_MAX_HOLD_MS}. The operation receives `paused`, its only way to load
   * or nest a pause without waiting on itself.
   */
  runWithLoadsPaused<T>(
    operation: (paused: PausedActorDatabaseLifecycle<Loaded>) => Promise<T>,
  ): Promise<T>;
  /** Notified whenever the published connection changes. */
  subscribe(listener: () => void): () => void;
}

/** Whether `error`, or anything in its cause chain, is a deadline expiry. */
function hasDeadlineCause(error: unknown): boolean {
  let current: unknown = error;
  for (let depth = 0; depth < 8 && current instanceof Error; depth += 1) {
    if (isDeadlineExceededError(current)) {
      return true;
    }
    if (
      current instanceof AggregateError &&
      current.errors.some((inner) => hasDeadlineCause(inner))
    ) {
      return true;
    }
    current = current.cause;
  }
  return false;
}

interface Pause {
  promise: Promise<void>;
  release: () => void;
  /** True while the operation's synchronous prefix runs: a call now is certainly re-entrant. */
  synchronous: boolean;
}

export function createActorDatabaseLifecycle<
  Connection,
  Loaded extends LoadedConnection<Connection>,
>(
  options: ActorDatabaseLifecycleOptions<Connection, Loaded>,
): ActorDatabaseLifecycle<Connection, Loaded> {
  type State = LifecycleState<Connection, Loaded>;
  const openMs = options.deadlines?.openMs ?? ACTOR_DATABASE_OPEN_DEADLINE_MS;
  const closeMs = options.deadlines?.closeMs ?? ACTOR_DATABASE_CLOSE_DEADLINE_MS;
  const pauseMaxHoldMs = options.deadlines?.pauseMaxHoldMs ?? ACTOR_DATABASE_PAUSE_MAX_HOLD_MS;
  const pausedLoadWaitMs =
    options.deadlines?.pausedLoadWaitMs ?? ACTOR_DATABASE_PAUSED_LOAD_WAIT_MS;
  let state: State = { kind: "closed" };
  let pause: Pause | null = null;
  const listeners = new Set<() => void>();

  const notify = (): void => {
    for (const listener of listeners) {
      listener();
    }
  };

  const closeBounded = (connection: Connection): Promise<void> =>
    withDeadline({ deadline: closeMs, operation: "Closing the study database" }, () =>
      options.close(connection),
    );

  const markDamaged = (attempt: OpenAttempt<Connection>, error: unknown): void => {
    if (attempt.damage !== undefined) {
      return;
    }
    attempt.damage = error;
    if (state.kind === "open" && state.attempt === attempt) {
      options.onError?.("connection damaged; latched failed so recovery replaces it", error);
      state = {
        attempt,
        connection: state.loaded.connection,
        error: new ActorDatabaseDamagedError(error),
        kind: "failed",
      };
      notify();
    }
  };

  /**
   * A timed-out open keeps running. Whatever it produces is never published: adopted into the
   * failed state it caused (so recovery closes it), or closed here once the lifecycle moved on.
   */
  const adoptLateOpen = (attempt: OpenAttempt<Connection>, work: Promise<Loaded>): void => {
    void work.then(
      (loaded) => {
        if (state.kind === "failed" && state.attempt === attempt) {
          state = { ...state, connection: loaded.connection };
          return;
        }
        closeBounded(loaded.connection).catch((error: unknown) => {
          options.onError?.("closing a connection from a timed-out open failed", error);
        });
      },
      () => {
        // The open failed after all; its own error path owned the cleanup.
      },
    );
  };

  const startOpen = (): Promise<Loaded> => {
    // Owned as soon as the engine open returns, before migrations run: a
    // migration failure must be able to close the handle it never published.
    const attempt: OpenAttempt<Connection> = { damage: undefined, owned: null };
    const own = (connection: Connection): void => {
      attempt.owned = connection;
      if (state.kind === "failed" && state.attempt === attempt && state.connection === null) {
        // A timed-out open that owns its engine only now: recovery must close it.
        state = { ...state, connection };
      }
    };
    // The async wrapper turns even a synchronous throw from `open` into a
    // rejection observed AFTER the opening state below is published.
    const work = (async () =>
      options.open(own, (error) => {
        markDamaged(attempt, error);
      }))();
    const run = async (): Promise<Loaded> => {
      try {
        const loaded = await withDeadline(
          { deadline: openMs, operation: "Opening the study database" },
          () => work,
        );
        if (attempt.damage !== undefined) {
          // Damaged before it was ever published: fail the open, keep the handle for recovery.
          const error = new ActorDatabaseDamagedError(attempt.damage);
          state = { attempt, connection: loaded.connection, error, kind: "failed" };
          throw error;
        }
        state = { attempt, kind: "open", loaded };
        return loaded;
      } catch (error) {
        if (state.kind !== "failed" || state.attempt !== attempt) {
          state = { attempt, connection: attempt.owned, error, kind: "failed" };
        }
        if (isDeadlineExceededError(error)) {
          adoptLateOpen(attempt, work);
        }
        throw error;
      } finally {
        notify();
      }
    };
    // An async function never settles synchronously, so this state is published
    // before any transition inside `run` can replace it; a concurrent caller
    // joins this promise instead of starting a second open.
    const promise = run();
    state = { attempt, kind: "opening", promise };
    return promise;
  };

  const loadPast = async (ignorePause: boolean): Promise<Loaded> => {
    switch (state.kind) {
      case "open": {
        return state.loaded;
      }
      case "opening": {
        // Bounded by construction: the open itself runs under its deadline.
        return state.promise;
      }
      case "closing": {
        // Bounded by construction: the close itself runs under its deadline.
        await state.promise.catch(() => {});
        return loadPast(ignorePause);
      }
      case "failed": {
        // Latched: recovery is an explicit close, never the next query.
        throw state.error;
      }
      case "closed": {
        const held = ignorePause ? null : pause;
        if (held !== null) {
          if (held.synchronous) {
            throw new ReentrantActorDatabasePauseError("load");
          }
          await withDeadline(
            {
              deadline: pausedLoadWaitMs,
              operation: "Opening the study database (waiting for an account switch to finish)",
            },
            () => held.promise,
          );
          return loadPast(ignorePause);
        }
        return startOpen();
      }
    }
  };

  const load = (): Promise<Loaded> => loadPast(false);

  const close = async (): Promise<void> => {
    switch (state.kind) {
      case "closed": {
        return;
      }
      case "opening": {
        // An opening connection owns the engine even before it is published. Let
        // it settle into open/failed, then close whatever it produced.
        await state.promise.catch(() => {});
        return close();
      }
      case "closing": {
        return state.promise;
      }
      case "failed": {
        const failed = state;
        if (failed.connection !== null) {
          try {
            await closeBounded(failed.connection);
          } catch (error) {
            if (!hasDeadlineCause(error)) {
              // Answered with a refusal: still owned, still failed; recovery can try again.
              throw error;
            }
            const abandoned = new ActorDatabaseCloseAbandonedError(error);
            options.onError?.("close timed out; connection abandoned", error);
            if (state === failed) {
              state = { ...failed, connection: null, error: abandoned };
              notify();
            }
            throw abandoned;
          }
        }
        if (state === failed) {
          state = { kind: "closed" };
          notify();
        }
        return;
      }
      case "open": {
        const open = state;
        const closing = (async () => {
          try {
            await closeBounded(open.loaded.connection);
          } catch (error) {
            if (hasDeadlineCause(error)) {
              // Never answered: abandon the handle (its native ownership stays with it until its
              // own close confirms) and latch failed, rather than retain a connection that is
              // half-closed and wait on it again.
              const abandoned = new ActorDatabaseCloseAbandonedError(error);
              options.onError?.("close timed out; connection abandoned", error);
              state = { attempt: open.attempt, connection: null, error: abandoned, kind: "failed" };
              notify();
              throw abandoned;
            }
            // Unconfirmed close: retain ownership, exactly as before the attempt.
            state = open;
            options.onError?.("close failed; connection retained", error);
            throw error;
          }
          state = { kind: "closed" };
          notify();
        })();
        state = { kind: "closing", promise: closing };
        return closing;
      }
    }
  };

  const paused: PausedActorDatabaseLifecycle<Loaded> = {
    load: () => loadPast(true),
    runWithLoadsPaused: (operation) => operation(),
  };

  /**
   * Hold new opens while `operation` runs (an account switch closes, wipes and reopens; a load
   * in between would open the wrong file or mode).
   *
   * Bounded three ways:
   *   - a load waiting behind the pause fails after {@link ACTOR_DATABASE_PAUSED_LOAD_WAIT_MS}
   *     with a retryable timeout. It FAILS rather than proceeding: releasing a load mid-swap could
   *     open a half-swapped database (a wiped file before the account is stored, say), which is
   *     worse than a Suspense retry;
   *   - the operation itself may hold for {@link ACTOR_DATABASE_PAUSE_MAX_HOLD_MS}. Past that its
   *     caller is rejected, the pause is released, and — if the swap left the lifecycle closed —
   *     it latches `failed`, so loads still cannot open the half-swapped state; recovery (an
   *     explicit close) clears it, and so does the straggling operation if it ever finishes;
   *   - re-entry cannot deadlock: a load or pause issued synchronously from inside the operation
   *     fails at once with `ReentrantActorDatabasePauseError`, one issued after its first `await`
   *     fails at the load wait above. The operation's `paused` argument is the supported way in.
   */
  const runWithLoadsPaused = async <T>(
    operation: (scope: PausedActorDatabaseLifecycle<Loaded>) => Promise<T>,
  ): Promise<T> => {
    const held = pause;
    if (held !== null) {
      if (held.synchronous) {
        throw new ReentrantActorDatabasePauseError("pause");
      }
      // Bounded by construction: the hold resolves by its own backstop at the latest.
      await held.promise;
      return runWithLoadsPaused(operation);
    }
    let release: () => void = () => {};
    const promise = boundedPromise(
      {
        deadline: pauseMaxHoldMs + PAUSE_HOLD_BACKSTOP_MARGIN_MS,
        onDeadline: "resolve",
        operation: "Study database loads paused",
      },
      (resolve) => {
        release = resolve;
      },
    );
    const current: Pause = { promise, release, synchronous: true };
    pause = current;
    let running: Promise<T>;
    try {
      running = Promise.resolve(operation(paused));
    } catch (error) {
      running = Promise.reject(error);
    } finally {
      current.synchronous = false;
    }
    const holdOperation = "Study database account switch (loads paused)";
    try {
      return await withDeadline(
        { deadline: pauseMaxHoldMs, operation: holdOperation },
        () => running,
      );
    } catch (error) {
      if (
        error instanceof DeadlineExceededError &&
        error.operation === holdOperation &&
        state.kind === "closed"
      ) {
        // Latch before the hold is released below, so no waiting load can open the half-swapped
        // database in between.
        state = {
          attempt: null,
          connection: null,
          error: new ActorDatabaseSwapTimeoutError(error),
          kind: "failed",
        };
        notify();
      }
      throw error;
    } finally {
      if (pause === current) {
        pause = null;
      }
      current.release();
    }
  };

  return {
    close,
    forget: ({ notify: shouldNotify = true } = {}) => {
      state = { kind: "closed" };
      if (shouldNotify) {
        notify();
      }
    },
    getFailure: () => (state.kind === "failed" ? state.error : undefined),
    getLoaded: () => (state.kind === "open" ? state.loaded : undefined),
    load,
    publish: (loaded, { notify: shouldNotify = true } = {}) => {
      state = { attempt: null, kind: "open", loaded };
      if (shouldNotify) {
        notify();
      }
    },
    runWithLoadsPaused,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}
